import format from "date-fns/format";
import isValid from "date-fns/isValid";
import parse from "date-fns/parse";
import { isEmpty } from "lodash";
import { areas } from "./_options";

export const VALIDATE_IMPORT_FILE_TYPE = "validate-import-file" as const;

export type InvalidDataType = {
  type: typeof VALIDATE_IMPORT_FILE_TYPE;
  text: string;
  isValid: boolean;
  message?: string;
};

const invalid = (message?: string, text?: string): InvalidDataType => ({
  type: VALIDATE_IMPORT_FILE_TYPE,
  text: text ?? "Dữ liệu không hợp lệ",
  isValid: false,
  message,
});

/**
 * Validate functions
 */
export const validatePlainText = (text: any, errMsg?: string) => {
  try {
    return String(text);
  } catch (error) {
    return invalid(errMsg);
  }
};

export const validateEmail = (email: any, errMsg?: string) => {
  try {
    if (typeof email === "string") {
      return email;
    }

    if ("hyperlink" in email && "text" in email) {
      return email.text;
    }
    return invalid(errMsg);
  } catch (error) {
    return invalid(errMsg);
  }
};

export const validateNumber = (value: any, errMsg?: string) => {
  try {
    return isNaN(Number(value))
      ? invalid(errMsg ?? `Dữ liệu phải ở dạng số. Dữ liệu được nhập: '${value}'`)
      : value;
  } catch (error) {
    return invalid();
  }
};

export const validateDate = (date: any, formatter = "d/M/yyyy", errMsg?: string) => {
  try {
    const _date: Date =
      typeof date === "string"
        ? parse(date[0] === "'" ? date.slice(1) : date, formatter, new Date())
        : date;

    if (!isValid(_date)) {
      return invalid(errMsg);
    }

    const dateFormatted = format(_date, formatter);

    if (_date.getTime() < new Date().getTime()) {
      return invalid("Yêu cầu ngày lớn hơn hiện tại", dateFormatted);
    }

    return dateFormatted;
  } catch (error) {
    return invalid();
  }
};

export const validateDataOptions = (value: any, options: string[], errMsg?: string) => {
  try {
    const error = invalid(
      errMsg ??
        `Dữ liệu phải thuộc một trong số '${options.join(", ")}'. Dữ liệu được nhập: '${value}'`
    );

    if (typeof value !== "string") {
      return error;
    }

    return value && !isEmpty(value) && !options.includes(value as string) ? error : value;
  } catch (error) {
    return invalid();
  }
};

export const validateTripBusiness = (value: any, errMsg?: string) => {
  try {
    return (value.split("\n") as string[]).map((g) => {
      const [from, toAndDate] = g.split("-");
      const [to, date] = toAndDate.split(" (");

      return {
        from,
        to,
        date: parse(date.slice(0, -1), "d/M/yyyy HH:mm", new Date()),
      };
    }) as any;
  } catch (error) {
    return invalid(errMsg);
  }
};

export const validateGrantedArea = (data: any, errMsg?: string) => {
  try {
    const arr = String(data).split(";");

    /**
     * Check last item is empty -> remove it
     */
    if ((arr.at(-1) ?? "").trim() === "") {
      arr.splice(-1);
    }

    if (isEmpty(arr)) {
      return invalid(errMsg);
    }

    return arr.map((i) => {
      const str = i.toString().trim();
      const data = areas.find((a) => a.cases.includes(str.toUpperCase()));

      if (!data) {
        throw Error(str);
      }

      return data.return;
    });
  } catch (error: any) {
    return invalid(
      errMsg ??
        `Dữ liệu nhập chứa nội dung '${error.message}' không phù hợp. Dữ liệu đã nhập: '${data}'`
    );
  }
};

export const validateGrantTerm = (data: any, errMsg?: string) => {
  try {
    const [start, end] = String(data).split("-");

    if (!start || !end) {
      return invalid(`Dữ liệu đã nhập: '${data}'`);
    }

    const parseStart = parse(start.trim(), "d/M/yyyy", new Date());
    const parseEnd = parse(end.trim(), "d/M/yyyy", new Date());

    if (!isValid(parseStart) || !isValid(parseEnd)) {
      return invalid(errMsg ?? `Dữ liệu đã nhập: '${data}'`);
    }

    return data;
  } catch (error) {
    return invalid(`Dữ liệu đã nhập: '${data}'`);
  }
};
